import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Bell, CheckCircle2, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MaskedLine, Reveal } from "@/components/Reveal";

function Countdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!target) return null;
  const diff = Math.max(0, new Date(target).getTime() - now);
  const units = [
    { label: "Jours", value: Math.floor(diff / 86400000) },
    { label: "Heures", value: Math.floor(diff / 3600000) % 24 },
    { label: "Min", value: Math.floor(diff / 60000) % 60 },
    { label: "Sec", value: Math.floor(diff / 1000) % 60 },
  ];
  return (
    <div className="mt-6 flex justify-center gap-3 sm:gap-5" data-testid="drop-countdown">
      {units.map((u) => (
        <div key={u.label} className="w-20 sm:w-24 rounded-2xl border border-kay-line bg-white/80 backdrop-blur px-3 py-4 shadow-[0_10px_30px_rgba(232,160,191,0.15)]" data-testid={`countdown-${u.label.toLowerCase()}`}>
          <p className="font-display text-4xl sm:text-5xl tabular-nums leading-none">{String(u.value).padStart(2, "0")}</p>
          <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-kay-pink mt-2">{u.label}</p>
        </div>
      ))}
    </div>
  );
}

function SoonCard({ product, index, onChanged }) {
  const { isAdmin, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      await api.post(`/admin/products/${product.id}/images`, fd);
      toast.success("Photo ajoutée");
      onChanged?.();
    } catch (err) {
      toast.error("Échec de l'envoi de la photo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const notify = async (e) => {
    e.preventDefault();
    try {
      await api.post("/notify", { email: email || user?.email || "", product_id: product.id });
      setDone(true);
      toast.success("Tu seras informée dès son arrivée !");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <motion.article
      data-testid={`soon-card-${product.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.07, ease: [0.22, 1, 0.36, 1] }}
      className="group"
    >
      <div className="relative aspect-[3/4] rounded-2xl border border-kay-line bg-white overflow-hidden transition-transform duration-500 group-hover:scale-[1.02] shadow-[0_10px_30px_rgba(232,160,191,0.10)]">
        {product.images?.length ? (
          <img src={fileUrl(product.images[0])} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="absolute inset-3 border border-dashed border-kay-rosedeep/60 rounded-xl flex flex-col items-center justify-center gap-3 bg-kay-rose/40">
            <ImagePlus className="text-kay-pink/70" size={28} strokeWidth={1.5} />
            <p className="font-display italic text-lg text-kay-ink/50">Photo à venir</p>
          </div>
        )}
        <span className="absolute top-3 left-3 bg-kay-ink text-kay-rose font-mono text-xs font-bold tracking-widest px-2.5 py-1 rounded-full">
          COMING SOON
        </span>
        {isAdmin && (
          <button
            data-testid={`soon-photo-edit-${product.id}`}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="absolute bottom-3 inset-x-3 bg-white/90 backdrop-blur border border-kay-line rounded-full py-2 text-xs font-semibold flex items-center justify-center gap-2 hover:bg-kay-ink hover:text-white transition-colors"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
            {product.images?.length ? "Remplacer la photo" : "Ajouter une photo"}
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`soon-photo-input-${product.id}`} />
      </div>
      <div className="pt-4 px-1">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-kay-pink">{product.brand}</p>
        <h3 className="font-display text-xl sm:text-2xl leading-tight mt-1">{product.name}</h3>
        {isAdmin && product.waitlist > 0 && (
          <p className="font-mono text-[10px] text-kay-green font-bold mt-1" data-testid={`waitlist-count-${product.id}`}>
            {product.waitlist} intéressée{product.waitlist > 1 ? "s" : ""}
          </p>
        )}
        {done ? (
          <p className="mt-3 flex items-center gap-2 text-xs font-semibold text-kay-green" data-testid={`notify-done-${product.id}`}>
            <CheckCircle2 size={14} /> C'est noté ! Un email de confirmation vient d'être envoyé.
          </p>
        ) : open ? (
          <form onSubmit={notify} className="mt-3 flex gap-1.5" data-testid={`notify-form-${product.id}`}>
            <input
              type="email"
              required
              defaultValue={user?.email || ""}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Ton email"
              className="flex-1 min-w-0 rounded-full border border-kay-line bg-white px-3.5 py-2 text-xs outline-none focus:border-kay-pink"
              data-testid={`notify-email-${product.id}`}
            />
            <button type="submit" className="rounded-full bg-kay-ink text-white px-4 py-2 text-xs font-bold hover:bg-kay-pink transition-colors" data-testid={`notify-submit-${product.id}`}>
              OK
            </button>
          </form>
        ) : (
          <button
            data-testid={`notify-button-${product.id}`}
            onClick={() => setOpen(true)}
            className="mt-3 w-full rounded-full border border-kay-ink py-2.5 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-kay-ink hover:text-white transition-all"
          >
            <Bell size={14} /> Je veux être informée
          </button>
        )}
      </div>
    </motion.article>
  );
}

export default function ComingSoon() {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const load = () => {
    api.get("/products", { params: { coming_soon: true } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const soonBrands = (settings?.brands || []).filter((b) => b.coming_soon);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      setDone(true);
      toast.success("Tu seras la première informée !");
    } catch (err) {
      toast.error("Vérifie ton email et réessaie");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-10" data-testid="coming-soon-page">
      <div className="text-center">
        <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-6">Kay Beauty</p>
        <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tight" data-testid="coming-soon-title">
          <MaskedLine delay={0.1}>COMING</MaskedLine>
          <MaskedLine delay={0.25} className="italic text-kay-pink">SOON</MaskedLine>
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-6 text-kay-ink/60 text-base md:text-lg max-w-md mx-auto" data-testid="coming-soon-text">
            De nouvelles marques et de nouvelles pépites arrivent bientôt…
          </p>
        </Reveal>
        {settings?.drop_date && (
          <Reveal delay={0.55}>
            <p className="mt-10 font-mono text-[11px] tracking-[0.35em] uppercase text-kay-ink/50">Prochain drop dans</p>
            <Countdown target={settings.drop_date} />
          </Reveal>
        )}
      </div>

      {soonBrands.length > 0 && (
        <div className="mt-16" data-testid="coming-brands">
          <Reveal className="mb-8">
            <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-2">Marques à venir</p>
            <h2 className="font-display text-3xl sm:text-4xl">Bientôt <span className="italic">chez Kay</span></h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {soonBrands.map((b, i) => (
              <Reveal key={b.name} delay={i * 0.08}>
                <div className="relative rounded-2xl border border-kay-line bg-gradient-to-br from-kay-rose to-kay-rosedeep/50 p-7 h-40 flex flex-col justify-between overflow-hidden grain" data-testid={`coming-brand-${b.name.toLowerCase().replace(/\s/g, "-")}`}>
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-kay-wine/70">Coming soon</span>
                  <p className="font-display text-2xl sm:text-3xl leading-tight">{b.name}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      )}

      <div className="mt-20">
        <Reveal className="flex items-end justify-between mb-10 flex-wrap gap-3">
          <div>
            <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-2">Produits à venir</p>
            <h2 className="font-display text-3xl sm:text-4xl">En coulisses <span className="italic text-kay-pink">…</span></h2>
          </div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-kay-ink/40">
            {products.length} pépite{products.length > 1 ? "s" : ""} en préparation
          </p>
        </Reveal>
        {products.length > 0 ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
            {products.map((p, i) => (
              <SoonCard key={p.id} product={p} index={i} onChanged={load} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="aspect-[3/4] rounded-2xl border border-dashed border-kay-rosedeep/60 bg-kay-rose/30 flex flex-col items-center justify-center gap-3" data-testid={`coming-placeholder-${n}`}>
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-kay-pink">Coming soon</span>
                <p className="font-display italic text-xl text-kay-ink/40">Pépite mystère</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <Reveal delay={0.2} className="mt-20 max-w-md mx-auto">
        <p className="text-center font-mono text-[11px] tracking-[0.3em] uppercase text-kay-ink/50 mb-4">
          Toutes les nouveautés, en avant-première
        </p>
        {done ? (
          <div className="flex items-center justify-center gap-3 rounded-2xl border border-kay-line bg-white py-6 px-6" data-testid="newsletter-success">
            <CheckCircle2 className="text-kay-green" size={22} />
            <p className="text-sm font-semibold">C'est noté ! Tu seras informée en avant-première.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="flex gap-2" data-testid="newsletter-form">
            <input
              data-testid="newsletter-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Ton adresse email"
              className="flex-1 rounded-full border border-kay-line bg-white px-5 py-3.5 text-sm outline-none focus:border-kay-pink transition-colors"
            />
            <button
              data-testid="newsletter-submit-button"
              type="submit"
              className="rounded-full bg-kay-ink text-white px-6 py-3.5 text-sm font-semibold flex items-center gap-2 hover:bg-kay-pink transition-colors"
            >
              <Bell size={15} /> Me prévenir
            </button>
          </form>
        )}
      </Reveal>
    </div>
  );
}
